import * as yup from 'yup';
import { ObjectShape, TypeOfShape } from 'yup/lib/object';

import {
  BaseField,
  BooleanField,
  FieldType,
  NumberField,
  StringField,
} from '../../../client/forms/fields';

import {
  BooleanWidgetSchemaMap,
  NumberWidgetSchemaMap,
  StringWidgetSchemaMap,
} from './widgetSchemas';

/**
 * Test multiple schemas against the value to check if the value is valid.
 */
function validateMultipleSchemas(
  schemas: yup.AnySchema[],
  value: TypeOfShape<ObjectShape>
): boolean {
  return schemas.some((schema) => {
    try {
      return schema.validateSync(value);
    } catch (error) {
      return false;
    }
  });
}

/**
 * Schemas for validating the fields and form data provided by clients.
 * This validation occurs when hydrating a notification. This is not used to validate user input on a form.
 */

/**
 * Base schema used for all fields.
 */
export const baseFieldSchema: yup.SchemaOf<BaseField<keyof typeof FieldType>> = yup
  .object({
    type: yup.mixed<keyof typeof FieldType>().oneOf(Object.values(FieldType)).required(),
    key: yup.string().required(),
    value: yup.object(),
    validation: yup.object(),
    label: yup.string(),
  })
  .required();

/**
 * {@link StringField} schema.
 */
export const stringFieldSchema: yup.SchemaOf<
  Omit<StringField, 'widget'>
> = baseFieldSchema.clone().shape({
  value: yup.string(),
  type: yup.mixed<'string'>().required().equals([FieldType.string]),
  widget: yup
    .object()
    .test({
      name: 'Widget check',
      message: 'Invalid widget ${path}',
      test: (value) => {
        const widgets = Object.values(StringWidgetSchemaMap);
        return validateMultipleSchemas(widgets, value);
      },
    })
    .required(),
});

/**
 * {@link NumberField} schema.
 */
export const numberFieldSchema: yup.SchemaOf<
  Omit<NumberField, 'widget'>
> = baseFieldSchema.clone().shape({
  value: yup.number().optional(),
  type: yup.mixed<'number'>().required().equals([FieldType.number]),
  widget: yup
    .object()
    .test({
      name: 'Widget check',
      message: 'Invalid widget ${path}',
      test: (value) => {
        const widgets = Object.values(NumberWidgetSchemaMap);
        return validateMultipleSchemas(widgets, value);
      },
    })
    .required(),
});

/**
 * {@link BooleanField} schema.
 */
export const booleanFieldSchema: yup.SchemaOf<
  Omit<BooleanField, 'widget'>
> = baseFieldSchema.clone().shape({
  value: yup.boolean().optional(),
  type: yup.mixed<'boolean'>().required().equals([FieldType.boolean]),
  widget: yup
    .object()
    .test({
      name: 'Widget check',
      message: 'Invalid widget ${path}',
      test: (value) => {
        const widgets = Object.values(BooleanWidgetSchemaMap);
        return validateMultipleSchemas(widgets, value);
      },
    })
    .required(),
});

/**
 * Lookup {@link FormField.type} to get the schema to validate against.
 */
const fieldSchemaMap = {
  string: stringFieldSchema,
  number: numberFieldSchema,
  boolean: booleanFieldSchema,
} as const;

/**
 * Form schema to validate a form data from a client.
 * This is not used to validate form input from the user.
 */
export const formSchema = yup
  .array()
  .of(
    yup.mixed().test({
      name: 'Field check',
      message: 'Invalid field ${path}',
      test: (value) => {
        const errors: Error[] = [];
        const valid = Object.values(fieldSchemaMap).some((schema) => {
          try {
            return schema.validateSync(value);
          } catch (e) {
            errors.push(e);
            return false;
          }
        });
        if (!valid) {
          throw new Error(errors.map((x) => x.message).join('.\n'));
        }
        return valid;
      },
    })
  )
  .transform((val) => (val?.length === 0 ? null : val))
  .required();
